import { ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { ChatItem } from '../../models/chat-ui.model';
import { GenerationService } from '../../services/generation/generation.service';
import { ChatCursorHintService } from '../../services/chat/presentation/chat-cursor-hint.service';
import { ChatDomEffectsService } from '../../services/chat/presentation/chat-dom-effects.service';
import { ChatFeedbackUiService } from '../../services/chat/presentation/chat-feedback-ui.service';
import { ChatIdService } from '../../services/chat/presentation/chat-id.service';
import { ChatMessageTextService } from '../../services/chat/presentation/chat-message-text.service';
import { ChatStorageService } from '../../services/chat/session/chat-storage.service';
import { FrontendLoggerService } from '../../services/frontend-logger.service';
import { LanguageService } from '../../services/language/language.service';
import { TaskNavigationService } from '../../services/task-navigation.service';
import { formatChatError } from './chat-runtime.models';
import { ChatMessageStatePort } from './chat-runtime.ports';

export class ChatMessageController {
  constructor(
    private readonly state: ChatMessageStatePort,
    private readonly text: ChatMessageTextService,
    private readonly tasks: TaskNavigationService,
    private readonly router: Router,
    private readonly generation: GenerationService,
    private readonly ids: ChatIdService,
    private readonly feedback: ChatFeedbackUiService,
    private readonly storage: ChatStorageService,
    private readonly dom: ChatDomEffectsService,
    private readonly cursorHint: ChatCursorHintService,
    private readonly language: LanguageService,
    private readonly logger: FrontendLoggerService,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  normalize(html: string): string { return this.text.normalize(html); }

  display(message: ChatItem): string {
    return message.type === 'system'
      ? this.normalize(message.content || '')
      : message.content || '';
  }

  shouldRenderHtml(message: ChatItem): boolean {
    return message.type === 'system'
      && /<([a-z][\w-]*)(?:\s[^>]*)?>/i.test(message.content || '');
  }

  openTask(taskId: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    try { this.tasks.setTaskId(taskId); } catch { }
    void this.router.navigate(['/tasks']);
  }

  openRoute(
    action: { route?: string; routeState?: Record<string, unknown> },
    event?: Event
  ): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (action?.route) {
      void this.router.navigate([action.route], { state: action.routeState || {} });
    }
  }

  openGenerate(
    action: {
      generate?: {
        sourceRefs?: string[];
        language?: string | null;
        expertRole?: string | null;
        message?: string | null;
        navigateToSummaries?: boolean;
      };
    },
    event?: Event
  ): void {
    event?.preventDefault();
    event?.stopPropagation();
    const payload = action?.generate;
    if (!payload?.sourceRefs?.length) return;

    this.generation.generateExecutiveSummary({
      type: 'executive_summary',
      sourceRefs: payload.sourceRefs,
      language: payload.language || 'es',
      expertRole: payload.expertRole || null,
      message: payload.message || undefined,
    }).subscribe({
      next: () => {
        if (payload.navigateToSummaries) {
          void this.router.navigate(['/generate/documentos-generados']);
          return;
        }
        this.pushSystem(this.language.translate('chat.generation.enqueued'), true);
      },
      error: (error) => {
        const key = Number(error?.status || 0) === 403
          ? 'chat.generation.forbidden'
          : 'chat.generation.error';
        this.pushSystem(this.language.translate(key), false);
      },
    });
  }

  onChatClicked(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target || target.closest('.message')) return;
    const anchor = target.closest('a') as HTMLAnchorElement | null;
    const taskId = anchor ? this.taskIdFromHref(anchor.getAttribute('href') || '') : null;
    if (taskId) {
      this.navigateTask(taskId, event);
      return;
    }
    const taskElement = target.closest('[data-task]') as HTMLElement | null;
    const data = taskElement?.getAttribute('data-task') || '';
    const match = data.match(/^task\/(.+)$/);
    const dataTaskId = match?.[1] || data || null;
    if (dataTaskId) this.navigateTask(dataTaskId, event);
  }

  editToInput(message: { content: string }, formControl: { setValue(value: string): void }): void {
    try {
      formControl.setValue(this.normalize(message?.content ?? ''));
      this.dom.focusInputAtEnd('chat-input-message');
    } catch (error) {
      this.logger.error(
        'No se pudo copiar el mensaje al input: ' + formatChatError(error),
        'ChatComponent.editMessageToInput'
      );
    }
  }

  async copy(message: Pick<ChatItem, 'content'>, index: number): Promise<void> {
    try {
      await this.feedback.copyMessageToClipboard(message);
      this.state.copiedMessageIndex = index;
      setTimeout(() => {
        if (this.state.copiedMessageIndex === index) this.state.copiedMessageIndex = null;
      }, 2000);
    } catch (error) {
      this.logger.error(
        'Error copying message to clipboard: ' + formatChatError(error),
        'ChatComponent.copyMessage'
      );
    }
  }

  showCursorHint(message: ChatItem | null | undefined, event: MouseEvent): void {
    this.cursorHint.show(this.state.cursorHintRef?.nativeElement, message, event);
  }
  hideCursorHint(): void { this.cursorHint.hide(this.state.cursorHintRef?.nativeElement); }

  selectMessage(message: ChatItem, loadSelectedChunks: () => void): void {
    if (!message || message.type !== 'system' || !message.id) return;
    this.state.selectedMessageId = String(message.id);
    try { this.storage.setSelectedMessageId(this.state.selectedMessageId); } catch { }
    loadSelectedChunks();
    this.changeDetector.markForCheck();
  }

  isSelected(message: ChatItem | null | undefined): boolean {
    return !!this.state.selectedMessageId
      && !!message?.id
      && String(message.id) === String(this.state.selectedMessageId);
  }

  deselect(event?: MouseEvent): void {
    try {
      event?.stopPropagation();
      event?.preventDefault();
    } catch (error) {
      this.logDeselectError(error);
    }
    this.state.selectedMessageId = null;
    try { this.storage.clearSelectedMessageId(); } catch (error) { this.logDeselectError(error); }
  }

  disposeCursorHint(): void { this.cursorHint.dispose(); }

  private pushSystem(content: string, withProgressLink: boolean): void {
    const item: any = {
      type: 'system',
      content,
      timestamp: new Date(),
      entityId: this.ids.createMessageEntityId(),
    };
    if (withProgressLink) {
      item.actions = [{
        kind: 'route',
        label: this.language.translate('chat.generation.viewProgress'),
        route: '/generate/documentos-generados',
        routeState: {},
      }];
    }
    this.state.messages.push(item);
    this.changeDetector.markForCheck();
  }

  private taskIdFromHref(href: string): string | null {
    return href.match(/\/task\/(.+)$/)?.[1]
      || href.match(/.*\/task\/(.+)/)?.[1]
      || null;
  }

  private navigateTask(taskId: string, event: MouseEvent): void {
    try { this.tasks.setTaskId(taskId); } catch { }
    void this.router.navigate(['/tasks']);
    event.preventDefault();
  }

  private logDeselectError(error: unknown): void {
    this.logger.error(
      'Error deselecting message: ' + formatChatError(error),
      'ChatComponent.deselectMessage'
    );
  }
}
